import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MenuItem } from '../../../core/interfaces/menu-item';
import { LaunchMenuResponse } from '../../../core/interfaces/launch-menu-response';
import { LocalStorageService } from '../localStorage/local-storage.service';

interface MenusApiResponse {
  menus: MenuItem[];
}

interface LaunchMenuApiResponse {
  launch: LaunchMenuResponse;
}

@Injectable({
  providedIn: 'root',
})
export class HomeMenuService {
  private readonly baseApiUrl = `${environment.baseUrl}${environment.apiPrefix}/v1`;

  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
  ) {}

  /**
   * Árvore de menus do usuário autenticado. Sem parâmetro de contexto: quem
   * decide a grade é o token — a API resolve o inquilino pela claim `context` e
   * recorta pelas claims de perfil.
   */
  async getHomeMenus(): Promise<MenuItem[]> {
    const response = await lastValueFrom(
      this.httpClient.get<MenusApiResponse>(`${this.baseApiUrl}/menus`, {
        headers: this.getHeaders(),
      }),
    );

    return response.menus ?? [];
  }

  async launchMenu(menuId: number): Promise<LaunchMenuResponse> {
    const response = await lastValueFrom(
      this.httpClient.post<LaunchMenuApiResponse>(
        `${this.baseApiUrl}/menus/${menuId}/launch`,
        {},
        { headers: this.getHeaders() },
      ),
    );

    return response.launch;
  }

  /**
   * Headers das chamadas de menu. Sem token válido a requisição NÃO é enviada:
   * antes, o header Authorization era simplesmente omitido e a chamada seguia
   * anônima, resultando em 401 sem nenhuma pista do motivo.
   */
  private getHeaders(): HttpHeaders {
    const token = String(this.localStorageService.get('bearerToken') ?? '').trim();

    if (!token || token === 'undefined' || token === 'null') {
      console.error(
        'Sessão sem bearerToken válido no localStorage — chamada de menus abortada.',
        { token },
      );
      throw new Error('Sessão expirada. Faça login novamente.');
    }

    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'Ocp-Apim-Subscription-Key': environment.ocpapimsubscriptionkey,
    });
  }
}
